import { useNostr } from '@nostrify/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCurrentUser } from './useCurrentUser';
import { useToast } from './useToast';

/**
 * Follow / unfollow a single user by rewriting the current user's kind 3
 * contact list. Preserves existing follows, relay hints, and petnames.
 */
export function useFollowUser(targetPubkey: string) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (follow: boolean) => {
      if (!user) throw new Error('You must be logged in to follow users.');

      const [existing] = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal: AbortSignal.timeout(8000) },
      );

      const tags: string[][] = existing
        ? existing.tags.filter(([name]) => name === 'p')
        : [];
      const content = existing?.content ?? '';

      const has = tags.some(([, pk]) => pk === targetPubkey);
      let next = tags;
      if (follow && !has) next = [...tags, ['p', targetPubkey]];
      if (!follow && has) next = tags.filter(([, pk]) => pk !== targetPubkey);

      const event = await user.signer.signEvent({
        kind: 3,
        content,
        tags: next,
        created_at: Math.floor(Date.now() / 1000),
      });
      await nostr.event(event, { signal: AbortSignal.timeout(8000) });
      return follow ? 'followed' : 'unfollowed';
    },
    onError: (error) => {
      toast({
        title: 'Could not update follows',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/**
 * Follow every member of a pack in one kind 3 rewrite (Option A: bulk follow
 * with confirm dialog in the UI). Reports how many were newly added.
 */
export function useFollowAll(memberPubkeys: string[]) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('You must be logged in to follow users.');

      const [existing] = await nostr.query(
        [{ kinds: [3], authors: [user.pubkey], limit: 1 }],
        { signal: AbortSignal.timeout(8000) },
      );

      const tags: string[][] = existing
        ? existing.tags.filter(([name]) => name === 'p')
        : [];
      const content = existing?.content ?? '';
      const known = new Set(tags.map(([, pk]) => pk));

      let added = 0;
      const next = [...tags];
      for (const pk of memberPubkeys) {
        if (pk === user.pubkey) continue;
        if (!known.has(pk)) {
          known.add(pk);
          next.push(['p', pk]);
          added += 1;
        }
      }

      const event = await user.signer.signEvent({
        kind: 3,
        content,
        tags: next,
        created_at: Math.floor(Date.now() / 1000),
      });
      await nostr.event(event, { signal: AbortSignal.timeout(8000) });
      return added;
    },
    onError: (error) => {
      toast({
        title: 'Could not follow everyone',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    },
  });
}

/** Read the current user's follow list (kind 3 `p` tags) for filter + button state. */
export function useMyFollowsQuery(enabled: boolean) {
  const { nostr } = useNostr();
  const { user } = useCurrentUser();

  return useQuery({
    queryKey: ['my-follows', user?.pubkey ?? ''],
    queryFn: async (c: { signal: AbortSignal }) => {
      if (!user) return [] as string[];
      const [event] = await nostr.query([{ kinds: [3], authors: [user.pubkey], limit: 1 }], {
        signal: c.signal,
      });
      if (!event) return [] as string[];
      return event.tags.filter(([n]) => n === 'p').map(([, pk]) => pk as string);
    },
    enabled: enabled && Boolean(user),
    staleTime: 2 * 60 * 1000,
  });
}
